import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import { ApplicationLearningSummary } from '../types';
import ApplicationCard from './ApplicationCard';
import { AppPagination } from '../../../components/ui/AppPagination';
import { PAGE_SIZE } from '../constants';

interface ApplicationsListProps {
  items: ApplicationLearningSummary[];
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (p: number) => void;
  onDetails: (id: number) => void;
  onRetry: () => void;
}

const ApplicationsList: React.FC<ApplicationsListProps> = ({
  items,
  loading,
  error,
  page,
  totalItems,
  onPageChange,
  onDetails,
  onRetry,
}) => {
  const from = totalItems > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(page * PAGE_SIZE, totalItems);

  const displayItems = items.slice(0, PAGE_SIZE);

  if (loading && displayItems.length === 0) {
    return (
      <div className="flex justify-center items-center py-20">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center text-red-600">
        <p className="font-semibold">Ошибка загрузки данных</p>
        <p className="text-sm mt-1 text-red-400">{error}</p>
        <button
          onClick={onRetry}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
        >
          Повторить
        </button>
      </div>
    );
  }

  if (displayItems.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-12 flex flex-col items-center justify-center text-center">
        <AssessmentOutlinedIcon fontSize="large" className="text-gray-300 mb-3" />
        <p className="text-gray-500 font-medium">Заявки не найдены</p>
        <p className="text-sm text-gray-400 mt-1">
          Попробуйте изменить параметры поиска или фильтры
        </p>
      </div>
    );
  }

  return (
    <div className="relative space-y-4 pt-1">
      {loading && (
        <div className="absolute top-[-10px] left-1/2 -translate-x-1/2 z-10 bg-white/90 p-1.5 rounded-full shadow-sm">
          <CircularProgress size={20} />
        </div>
      )}

      <div className="flex flex-col gap-4">
        {displayItems.map(item => (
          <ApplicationCard key={item.application_id} item={item} onDetails={onDetails} />
        ))}
      </div>

      <div className="mt-6 bg-white border border-gray-200 rounded-xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
        <div className="text-sm text-gray-700">
          Показано <span className="font-medium">{from}</span> - <span className="font-medium">{to}</span> из <span className="font-medium">{totalItems}</span>
        </div>

        <AppPagination
          page={page}
          totalItems={totalItems}
          pageSize={PAGE_SIZE}
          onChange={onPageChange}
        />
      </div>
    </div>
  );
};

export default ApplicationsList;